import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface UserData {
  descricao: string;
  codigo: number;
  unidade: number;
  tipo: number;
  empresa: number;
  accessToken: string;
  refreshToken: string;
  CLIENTES: string | null;
  VENDEDORES: string | null;
  CUENTA_CORRIENTE: string | null;
  LISTA_PRECIO: string | null;
  METAS: string | null;
  PEDIDOS: string | null;
  STOCK: string | null;
  vendedorCod: number | null;
  // true si el usuario puede ver todos los pedidos (admin o supervisor)
  pedidosTodos?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly STORAGE_KEY = 'agrofuturo_user_data';
  
  private currentUserSubject = new BehaviorSubject<UserData | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  
  constructor() {
    this.loadSavedUser();
  }
  
  /**
   * Guarda los datos del usuario después del login
   */
  setUserData(userData: UserData): void {
    this.currentUserSubject.next(userData);
    this.saveUserData(userData);
  }
  
  /**
   * Obtiene los datos del usuario actual
   */
  getCurrentUser(): UserData | null {
    return this.currentUserSubject.value;
  }
  
  /**
   * Obtiene el código del vendedor actual
   */
  getVendedorId(): number | null {
    const userData = this.currentUserSubject.value;
    return userData?.vendedorCod ? userData.vendedorCod : 0;
  }

  /**
   * Indica si el usuario puede ver pedidos de todos los vendedores
   * (admin sin vendedor vinculado o supervisor). El flag `pedidosTodos`
   * viene del login; para sesiones guardadas sin el campo se usa la
   * ausencia de vendedor vinculado como criterio.
   */
  puedeVerTodosPedidos(): boolean {
    const userData = this.currentUserSubject.value;
    if (!userData) return false;
    return !userData.vendedorCod || !!userData.pedidosTodos;
  }

  /**
   * Indica si el usuario puede administrar el módulo Stock por Lotes
   * (alta/edición/eliminación/importación). Permiso dedicado
   * PERMISOS_CRM.STOCK = '1'; los usuarios sin vendedor vinculado se
   * consideran admins del sistema, igual que en el backend.
   */
  puedeAdministrarStock(): boolean {
    const userData = this.currentUserSubject.value;
    if (!userData) return false;
    return !userData.vendedorCod || userData.STOCK === '1';
  }
  
  /**
   * Cierra la sesión del usuario
   */
  logout(): void {
    this.currentUserSubject.next(null);
    localStorage.removeItem(this.STORAGE_KEY);
  }
  
  /**
   * Carga los datos guardados del usuario desde localStorage
   */
  private loadSavedUser(): void {
    try {
      const savedData = localStorage.getItem(this.STORAGE_KEY);
      if (savedData) {
        const userData: UserData = JSON.parse(savedData);
        this.currentUserSubject.next(userData);
      }
    } catch (error) {
      console.warn('UserService: Error al cargar datos del usuario:', error);
    }
  }
  
  /**
   * Guarda los datos del usuario en localStorage
   */
  private saveUserData(userData: UserData): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(userData));
    } catch (error) {
      console.warn('UserService: Error al guardar datos del usuario:', error);
    }
  }
}
